<template lang='pug'>
section.canned-response-list(:class="{ 'simple-list': useSimpleList }")
  form.canned-response-search(
    autocomplete="off"
    v-on:submit.prevent="")
    fieldset
      label(for="searchCannedResponses") Search canned responses by Title or Tag
      div.input-group
        div.search-input-wrapper
          input.search-input(
            type="text"
            id="searchCannedResponses"
            placeholder="Search by title or tag name"
            v-model="searchQuery"
            ref="searchInput")
          button.search-clear-query(
            @click.prevent="searchQuery = ''"
            value="Clear search input")
            svg.svgicon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512")
              path(d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z")

  section.canned-response-filter
    fieldset
      label Filter responses
      ul.filter-bar
        li
          dl.dropdown-menu
            dt(@click="filtersStatus.author = !filtersStatus.author") By author
            transition(name="slide-fade")
              dd(
                v-if="filtersStatus.author" v-on-click-outside="closeFilterDropdown")
                .radio-group(
                  v-if="authorList.length > 0"
                  v-for="author in authorList")
                  input(
                    type="radio"
                    :name="'author-checkbox-' + author"
                    :id="'author-checkbox-' + author"
                    :value="author"
                    v-model="filterAuthor")
                  label(:for="'author-checkbox-' + author") {{ author }}
        li
          dl.dropdown-menu
            dt(@click="filtersStatus.tag = !filtersStatus.tag") By tag
            transition(name="slide-fade")
              dd(
                v-if="filtersStatus.tag"
                v-on-click-outside="closeFilterDropdown")
                .checkbox-group(
                  v-if="tagList.length > 0"
                  v-for="tag in tagList")
                  input(
                    type="checkbox"
                    :name="'tag-checkbox-' + tag"
                    :id="'tag-checkbox-' + tag"
                    :value="tag"
                    v-model="filterTags")
                  label(:for="'tag-checkbox-' + tag") {{ tag }}

    //- Applied filter list
    ul.applied-filters(v-if="activeFilters")
      li
        button.tag.primary(@click="clearAllFilters()") Remove all filters
        //- Array filter - checkbox
      template(
        v-if="Array.isArray(filterTags)"
        v-for="tag in filterTags")
        li
          button.tag.primary(@click="clearFilter('tag', tag)") Tag:&nbsp;
            b {{ tag }}
      //- Array filter - string
      template(v-if="filterAuthor.length > 0")
        li
          button.tag.primary(@click="clearFilter('author')") Author:&nbsp;
            b {{ filterAuthor }}

  template(v-if="useSimpleList")
    //- Simplified UI for coaching chat
    ul.simple-list
      li(
        v-if="listData.length > 0"
        v-for="item in listData"
        :title="item.title")
        | {{ item.title }}
        button.btn.small.success.btn-add-canned-response(
          @click.prevent="selectResponse(item)")
          | Fill &nbsp;
          svg.svgicon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512")
            path(d="M376 232H216V72c0-4.42-3.58-8-8-8h-32c-4.42 0-8 3.58-8 8v160H8c-4.42 0-8 3.58-8 8v32c0 4.42 3.58 8 8 8h160v160c0 4.42 3.58 8 8 8h32c4.42 0 8-3.58 8-8V280h160c4.42 0 8-3.58 8-8v-32c0-4.42-3.58-8-8-8z")

  template(v-else)
    //- Listing view
    div.table-wrapper
      vue-table(
        :columns="columnLabels"
        :rows="sortedListData"
        :sort-by="sort.by"
        :sort-direction="sort.direction"
        @column:sort="onSort")
        template(slot-scope="{ column }" slot="header__actions")
          div.align-right Actions
        template(slot-scope="{ row }" slot="tags")
          div.tags
            span.tag(v-for="tag in row.tags") {{ tag }}
        template(slot-scope="{ row }" slot="actions")
          div.button-wrapper
            button.btn.small.success(@click="selectResponse(row)") Edit
            button.btn.small.error(@click="deleteResponse(row)") Delete

</template>

<script>

// Provides fuzzy search
import Fuse from 'fuse.js';
// Filter dropdown
import { mixin as onClickOutside } from 'vue-on-click-outside';

// Provides sortable table
import VueTable from '@lossendae/vue-table';

export default {
  name: 'CannedResponseList',
  props: [
    'source-list-data',
    'use-simple-list',
    'trigger-search-input-focus'
  ],
  components: {
    VueTable
  },
  data() {
    return {
      selectedResponse: {},
      listData: this.sourceListData,
      // Search
      searchQuery: '',
      // Filter
      filterAuthor: '',
      filterTags: [],
      filtersStatus: {
        author: false,
        tag: false
      },
      activeFilters: false,
      // Powers author filter
      authorList: [],
      tagList: [],
      // VueTable config
      columnLabels: [{
        name: 'title',
        title: 'Title',
        sortable: true
      }, {
        name: 'author',
        title: 'Author',
        sortable: true
      }, {
        name: 'tags',
        title: 'Tags'
      }, {
        name: 'actions',
        title: 'Actions'
      }],
      sort: {
        by: 'name',
        direction: 'asc'
      }
    };
  },
  computed: {
    sortedListData() {
      const me = this;
      return this.listData.sort((a, b) => {
        if (me.sort.direction === 'asc') {
          if (a[me.sort.by] < b[me.sort.by]) { return -1; }
          if (a[me.sort.by] > b[me.sort.by]) { return 1; }
          return 0;
        }

        if (b[me.sort.by] < a[me.sort.by]) { return -1; }
        if (b[me.sort.by] > a[me.sort.by]) { return 1; }
        return 0;
      });
    }
  },
  methods: {
    init() {
      this.listData = this.sourceListData;

      // Setup filters
      this.setAuthorList();
      this.setTagList();

      // Focus search input
      if (this.triggerSearchInputFocus) {
        this.focusSearchInput();
      }
    },
    focusSearchInput() {
      setTimeout(() => { this.$refs.searchInput.focus(); }, 300);
    },
    selectResponse(response) {
      this.selectedResponse = response;
      this.$emit('select-canned-response', this.selectedResponse);
    },
    deleteResponse(response) {
      this.$emit('delete-canned-response', response);
    },
    // Filter methods
    closeFilterDropdown(filterKey) {
      if (filterKey && filterKey.length > 0) {
        this.filtersStatus[filterKey] = false;
      } else {
        // Close all filters
        for (const filter in this.filtersStatus) {
          this.filtersStatus[filter] = false;
        }
      }
    },
    setAuthorList() {
      this.authorList = this.sourceListData.reduce((authorList, response) => {
        if (!authorList.includes(response.author)) {
          authorList.push(response.author);
        }
        return authorList;
      }, []);
    },
    setTagList() {
      // Collect tags
      this.tagList = this.sourceListData.reduce((tagList, response) => {
        // Prevent errors
        if (!response.tags || response.tags.length === 0) {
          return tagList;
        }

        response.tags.map(tag => {
          if (!tagList.includes(tag)) { tagList.push(tag); }
        });

        return tagList;
      }, []);
    },
    setActiveFilters() {
      let filtersSet = false;

      if (Array.isArray(this.filterTags) && this.filterTags.length > 0) {
        // Tags
        filtersSet = true;
      } else if (this.filterAuthor && this.filterAuthor.length > 0) {
        // Author
        filtersSet = true;
      }

      this.activeFilters = filtersSet;
    },
    clearFilter(filterKey, arrayItem) {
      if (filterKey === 'author') {
        this.filterAuthor = '';
      } else if (filterKey === 'tag') {
        this.filterTags = this.filterTags.reduce((acc, tag) => {
          if (tag !== arrayItem) { acc.push(tag); }
          return acc;
        }, []);
      }
    },
    clearAllFilters() {
      this.filterAuthor = '';
      this.filterTags = [];
      this.activeFilters = false;
    },
    runSearch(query) {
      // Reset list if query empty
      if (query.length === 0) {
        this.listData = this.sourceListData;
        this.filterListData();
        return;
      }

      // Filter listData before preforming search
      if (this.activeFilters) { this.filterListData(); }

      // Use listData as source if we have activeFilters
      const sourceListDataCopy = this.activeFilters ? Object.assign([], this.listData) : Object.assign([], this.sourceListData);

      const fuseOptions = {
        shouldSort: true,
        threshold: 0.4,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: [
          'title',
          'tags'
        ]
      };

      let fuse = new Fuse(sourceListDataCopy, fuseOptions);
      this.listData = fuse.search(query);
    },
    filterListData() {
      const hasAuthorFilter = this.filterAuthor.length > 0;
      const hasTagFilters = this.filterTags.length > 0;

      // Reset list data if no filters are set
      if (!hasAuthorFilter && !hasTagFilters) {
        this.listData = this.sourceListData;
        return;
      }

      this.listData = this.sourceListData.reduce((acc, response) => {
        if (hasAuthorFilter && response.author === this.filterAuthor) {
          // Author
          acc.push(response);
        } else if (hasTagFilters) {
          // Tags
          for (const tag of this.filterTags) {
            if (response.tags && response.tags.includes(tag)) {
              acc.push(response);
              break;
            }
          }
        }

        return acc;
      }, []);
    },
    onSort({ sortBy, sortDirection }) {
      this.sort.by = sortBy;
      this.sort.direction = sortDirection;
    }
  },
  watch: {
    triggerSearchInputFocus(value) {
      if (value) {
        this.focusSearchInput();
      }
    },
    searchQuery(query) {
      this.runSearch(query);
    },
    sourceListData() {
      this.init();

      // When data changes, keep updated to match filter and search
      this.filterListData();
      this.runSearch(this.searchQuery);
    },
    // Filters
    filterAuthor() {
      // Radio input - close after change
      this.closeFilterDropdown('author');

      this.setActiveFilters();
      this.filterListData();
    },
    filterTags() {
      this.setActiveFilters();
      this.filterListData();
    }
  },
  mounted() {
    this.init();
  },
  mixins: [ onClickOutside ]
};
</script>

<style lang="scss">
// NOTE !UNSCOPED!
// - Only use for overrides to VueTable styling
.table-wrapper {
  background-color: $white;

  .d-flex {
    display: -webkit-box !important;
    display: -ms-flexbox !important;
    display: flex !important;
  }
  table {
    table-layout: auto;
    border: 1px solid #ddd;

    tr {
      border-bottom: 1px solid #bdbdbd;
      box-sizing: border-box;

      &:last-child {
        border-bottom: 0px solid #fff;
      }

      th, td {
        padding: $xs-spacing $small-spacing;
      }
    }
    td {
      border-bottom: 0px;
    }
    thead tr {
      background-color: $light-grey;

      th {
        padding: $small-spacing;

        a {
          white-space: normal;
        }
      }
    }

    .button-wrapper {
      text-align: right;

      button {
        display: inline;
        margin: 0 6px 0 0;
        padding: 8px 16px;

        &:last-child {
          margin-right: 0;
        }
      }
    }
    .tags {
      padding: 0px 5px 3px;
      .tag {
        display: inline-block;
        background-color: $action-color;
        padding: 3px 6px;
        border-radius: 3px;
        margin: 6px 6px 0 0;
        color: #fff;
        line-height: 1.2;
      }
    }

    .align-right {
      text-align: right;
    }
  }
}
</style>
<style lang="scss" scoped>
section.canned-response-list {
  &.simple-list {
    .canned-response-filter .filter-bar li {
      min-width: 50%;
      max-width: 10em;
    }
  }

  fieldset {
    padding: 0;
    margin: 0 auto $small-spacing;
  }

  // Search
  form.canned-response-search {
    .input-group {
      display: flex;
      flex-flow: row wrap;
    }
    .search-input-wrapper {
      position: relative;
      display: inline-block;
      width: auto;
      flex-grow: 1;
      overflow: hidden;
      font-size: 16px;

      @include media-query-bp($bp6) {
        font-size: 18px;
      }

      .search-clear-query {
        position: absolute;
        right: 0px;
        top: 0px;
        padding: 8px 4px 9px 10px;
        line-height: 1.8;
        color: $base-font-color;
        cursor: pointer;

        @include media-query-bp($bp6) {
          padding: 8px 6px 9px 10px;
        }

        svg.svgicon {
          height: 24px;
          opacity: 0.5;
        }
      }
      .search-input {
        background-color: #fff;
      }
    }
    .search-canned-response-btn {
      display: inline-block;
    }
  }

  // Filters
  .canned-response-filter {
    .filter-bar {
      margin: 0 auto;
      width: 100%;
      display: flex;
      justify-content: space-apart;
      align-items: stretch;
      li {
        flex: 0 1 20%;
        min-width: 6.25em;
      }
    }
    .filter-btn {
      padding-left: 6px;
      .btn {
        display: block;
        width: 100%;
        height: 100%;
        padding: $xs-spacing $small-spacing;
      }
    }
    .dropdown-menu {
      @include zero;
      position: relative;
      dt {
        position: relative;
        margin: 0 auto;
        padding: $xs-spacing $small-spacing;
        background: #fff;
        border: 1px solid $md-grey-200;
        display: block;
        cursor: pointer;
        &:hover, &:focus {
          color: $action-color;
        }
      }
      dt::after {
        display: block;
        content: '';
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 7px solid $base-font-color;
        margin-top: -3px;
        @include position(absolute, 50% $xs-spacing null null);
      }
      dd {
        @include position(absolute, null 0 null 0);
        z-index: 100;
        @include zero;
        padding: $xs-spacing $small-spacing;
        background: #fff;
        border: 1px solid $md-grey-200;
        border-top: 0;
        @include drop-shadow;
        max-height: 60vh;
        overflow-y: auto;
        hr {
          margin: 0 auto $small-spacing;
        }
      }
      .dropdown-filter {
        margin: $xs-spacing auto $small-spacing;
        input {
          font-size: $ms-down1;
        }
      }
      .checkbox-group label,
      .radio-group label {
        font-size: $ms-down1;
      }
      .review-range-days {
        margin: 0.5em auto 0;
        width: 100%;
        display: flex;
        align-items: center;
        input {
          max-width: 3.5em;
          margin-right: 0.5em;
          margin-bottom: 0;
          padding: 0.25em 0.5em;
        }
      }
    }

    // TRANSITIONS
    .slide-fade-enter-active {
      transition: all .3s ease;
    }
    .slide-fade-leave-active {
      transition: all .25s cubic-bezier(1.0, 0.5, 0.8, 1.0);
    }
    .slide-fade-enter, .slide-fade-leave-to {
      transform: translateY(-25%);
      opacity: 0;
    }

    .applied-filters {
      margin: $small-spacing auto $xs-spacing;
      padding: 0;
      min-height: 2em;
      li {
        display: inline-block;
        margin-right: 4px;
        margin-bottom: 6px;
      }
      button.tag {
        position: relative;
        padding: 4px 8px;
        padding-left: 24px;
        border-radius: 1em;
        font-family: $sans-font-family;
        font-size: $ms-down1;
        text-transform: uppercase;
        color: $grey;
        cursor: hand;
        background-image: linear-gradient(to bottom, #fff 70%, $md-grey-100);
        border: 1px solid $md-grey-200;
        transition: all .25s ease;
        &:before {
          content: '\2716';
          display: block;
          position: absolute;
          left: 6px;
          top: 6px;
          color: $error-color !important;
          font-size: $ms-down1;
        }
        &:hover, &:focus {
          border-color: $action-color;
          color: $action-color;
          background: #fff;
          @include drop-shadow;
          outline: none;
        }
        &:active {
          outline: none;
          opacity: 0.6;
          margin-top: 1px;
          box-shadow: 0 0 0 rgba(#fff, 0);
          transform: scale(0.98);
        }
        &.primary {
          color: $action-color;
          font-weight: $base-font-weight-strong;
        }
      }
    }
  }

  ul.simple-list {
    background-color: #fff;

    li {
      position: relative;
      border-bottom: 1px solid #ddd;
      padding: 5px 5px 4px 10px;
      cursor: pointer;

      &:hover {
        background-color: $base-background-color;
      }

      &:last-child {
        border-bottom: 0px;
      }

      button.btn-add-canned-response {
        height: 100%;
        height: calc(100% + 1px);
        padding: 3px 12px;
        position: absolute;
        top: 0px;
        right: 0px;
        border-radius: 0px;

        svg.svgicon {
          height: auto;
          width: 12px;
          margin: 0px;
          position: relative;
          top: 2px;
          left: 2px;
        }
      }
    }
  }
}
</style>
